import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit } from '@angular/core';
import * as moment from 'moment-timezone';
import {
  isNumeric,
  RowDataDto,
  StatsDto,
  ValueDefDto,
} from 'src/app/shared/flight-log-api/stats/stats-client.model';
import { indexNotIn } from 'src/util/array';
import { round } from 'src/util/math';

interface ColumnConfig<T> extends Partial<ValueDefDto> {
  formatter?: (value: T, column: Column<T>) => string;
  customTemplate?: boolean;
  display?: boolean;
}

interface Column<T = any> extends ColumnConfig<T> {
  numeric: boolean;
  display: boolean;
  memberType: string;
}

interface ColumnConfigMap {
  [name: string]: ColumnConfig<any>;
}
interface ValueColumnPair<T = any> {
  value: T;
  column: Column<T>;
}

interface Cell<T = any> {
  column: Column<T>;
  displayValue: string;
  value: T;
}

function withColumn<T>(valueDefs: ValueDefDto[], cfgs: ColumnConfigMap, memberType: string) {
  return (value: any, index: number): ValueColumnPair<T> => {
    const valueDef = valueDefs[index];
    const cfg = cfgs[valueDef.name];
    const cfgDefaults = Object.assign(valueDef, { display: true });
    const configured = Object.assign(cfgDefaults, cfg);
    const numeric = isNumeric(configured.dataType);
    const column = Object.assign({ numeric, memberType }, configured);
    return { value, column };
  };
}

const userTimeZone = moment.tz.guess();
function formatDate(format: string, nullValue: string = '') {
  return (dateString: any) => {
    const m = moment(dateString);
    if (m.isValid()) {
      return m.tz(userTimeZone).format(format);
    }
    return nullValue;
  };
}

function pad(num: number): string {
  if (num < 10) {
    return `0${num}`;
  }
  return num.toString();
}

const formatHourDuration = (hoursVal: number) => {
  if (hoursVal == null) {
    return null;
  }
  const hours = Math.trunc(hoursVal);
  const minutes = Math.round((hoursVal - hours) * 60);
  return `${pad(hours)}:${pad(minutes)}`;
};

const formatDutyStatus = (status: string) => {
  return status === 'Off Duty' ? 'Off Duty' : '';
};

/*

 The below column configurations allow you to set metadata for each column. The primary purpose is to:

 1) Override the display name using the 'displayName' property for the column (sets custom header name)
 2) Set a boolean 'display' property to set the visibility of the column
 3a) Provide a custom formatter function to render the string value for the column or
 3b) Set the 'customTemplate' property to true to indicate the template will do the nescessary display logic
     (see the Flight Log ID column for an example). The formatter function takes two arguments - the cell value
     provided by the stats service and the column configuration as defined both by the service and any overriden/custom
     properties defined here.
     TODO: provide another argument to the formatter function that provides
     the entire row values to allow for display logic that has depenencies on multiple columns


*/

const columnConfigs: ColumnConfigMap = {
  // Dimensions
  EmployeeId: { display: false },
  EntryTime: {
    formatter: formatDate('M/D'),
    displayName: 'Date',
  },
  ShiftId: {
    display: false,
  },
  ShiftStartTime: {
    formatter: formatDate('HH:mm', 'Off'),
    displayName: 'On',
  },
  ShiftEndTime: {
    formatter: formatDate('HH:mm', 'Duty'),
    displayName: 'Off',
  },
  FlightLogId: { displayName: 'Flight', customTemplate: true },
  FlightDepartureTime: {
    displayName: 'Depart',
    formatter: formatDate('H:mm'),
  },
  FlightLogMode: {
    displayName: 'Log Mode',
    display: false,
  },
  FlightSummaryId: {
    display: false,
  },
  FlightDispatchId: { displayName: 'Disp#', display: false },
  DutyStatus: {
    displayName: 'Shift',
    formatter: formatDutyStatus,
    display: false,
  },
  CompanyId: {
    displayName: 'Company',
    display: false,
  },
  CompanyName: { display: false },
  BaseId: {
    display: false,
  },
  BaseName: { display: false },
  AircraftId: { display: false },
  AircraftTailNumber: { displayName: 'ACFT' },
  AircraftMake: { display: false },
  AircraftModel: { displayName: 'ACFT Type', display: false },
  PilotId: { display: false },
  PilotFirstName: { display: false },
  PilotLastName: { display: false },
  EmailAddress: { display: false },

  // Measures
  DutyTime: { displayName: 'Duty Time' },
  PriorRest: { displayName: 'Rest' },
  FlightCount: { displayName: '# FLTS', display: false },
  DayLandings: { displayName: 'LDGS Day' },
  NightLandings: { displayName: 'LDGS Ngt' },
  Hnvgo: { displayName: 'HNVGO' },
  TotalNightLandings: { display: false },
  TotalLandings: { displayName: 'Total LDGS', display: false },
  DayFlightTime: { displayName: 'FLT Day' },
  NightUnaidedFlightTime: { displayName: 'FLT Ngt' },
  NightAidedFlightTime: { displayName: 'FLT NVG' },
  TotalFlightTime: { displayName: 'FLT Total', display: true },
  BlockTime: { displayName: 'Block HRS', display: false },
  FaaFlightTime: { displayName: 'FAA HRS', display: false },
  Part91FlightTime: { displayName: 'PT 91' },
  Part135FlightTime: { displayName: 'PT 135' },
  IfrImcFlightTime: { displayName: 'IFR IMC' },
  IfrSimFlightTime: { displayName: 'IFR SIM' },
};

const withDimColumn = (stats: StatsDto) => withColumn(stats.dimensions, columnConfigs, 'dimension');
const withMeasureColumn = (stats: StatsDto) => withColumn(stats.measures, columnConfigs, 'measure');

function toCell<T>(valueColumnPair: ValueColumnPair<T>): Cell<T> {
  const { value, column } = valueColumnPair;

  const rounded = value != null && column.numeric ? round(value, 1) : value;

  let displayValue = rounded != null ? String(rounded) : '';
  if (column.formatter != null) {
    displayValue = column.formatter(value, column);
  }
  return { displayValue, column, value };
}

@Component({
  selector: 'fl-airman-flight-duty-report',
  templateUrl: './airman-flight-duty-report.component.html',
  styleUrls: ['./airman-flight-duty-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AirmanFlightDutyReportComponent implements OnInit, OnChanges {
  @Input()
  public stats: StatsDto;

  public groupDimIndexes: number[] = [];
  public columns: Column[];

  constructor() {}

  public ngOnInit() {}

  public ngOnChanges() {
    if (this.stats != null) {
      this.groupDimIndexes = this.stats.group.dimensions.map((d) => d.index);
      this.columns = [
        ...this.stats.dimensions
          .map(withDimColumn(this.stats))
          // .filter(indexNotIn(this.groupDimIndexes))
          .map((vc) => vc.column)
          .filter((c) => c.display),

        ...this.stats.measures
          .map(withMeasureColumn(this.stats))
          .map((vc) => vc.column)
          .filter((c) => c.display),
      ];
    }
  }

  public getCells(rowData: RowDataDto) {
    const cells = [
      ...rowData.dimensionKeys
        .map(withDimColumn(this.stats))
        .filter(indexNotIn(this.groupDimIndexes))
        .map(toCell)
        .filter((c) => c.column.display),

      ...rowData.measureValues
        .map(withMeasureColumn(this.stats))
        .map(toCell)
        .filter((c) => c.column.display),
    ];
    return cells;
  }
}
